/*
 * Strict search terms.
 *
 * Shopify's storefront search widens a query the customer typed in two
 * separate ways, and both put wines from unrelated producers on the results
 * page:
 *
 *   1. Relaxation. When a strict all-terms match returns few hits, Shopify
 *      drops terms and pads the page with loosely-ranked filler, up to a ~1000
 *      cap. "Golan Heights Winery" (6 real hits) came back with 208 results.
 *      Passing the AND operator explicitly turns this off.
 *   2. Typo tolerance. Even with AND, an unquoted term still matches its near
 *      neighbours, so "Domaine Laroche" returned 11 wines of which 10 were
 *      Château Laroque and Larcis Ducasse, and "gin Bombay" — a brand the shop
 *      does not carry — returned 15 products, every one of them wrong. A term
 *      carrying a wildcard is matched as a prefix instead, exactly, which
 *      turns that off while still following Slovak inflection: kôš/koše,
 *      darčekový/darčekové.
 *
 * So each word is submitted as its own prefix term: "Domaine* AND Laroche*".
 * That is strictQuery(), and it is what the page fell back on until 2026-09-02.
 *
 * ----------------------------------------------------------------------------
 * 2026-09-02 — two things the customer reported that the above does not cover.
 *
 * (a) A term still matches ANY field, description included, and the catalogue's
 *     descriptions are prose. "burgundsk" returned 14 products and not one of
 *     them carries the word in its title, vendor, type or tags — they are
 *     Bordeaux and Alsace wines whose copy says "pripomína burgundský štýl".
 *     Honest full-text hits, but not what someone typing a region wants.
 *
 *     preciseQuery() therefore scopes every term to the four structured fields
 *     — title, vendor, product_type, tag — and the prose matches move to their
 *     own labelled block on the results page (assets/search-extra.js) instead
 *     of being dropped: "talianske červené víno" is 18 products full-text and 2
 *     scoped, so dropping them would have been a second bug.
 *
 * (b) Scoping alone makes a region searchable only in the language the
 *     catalogue happens to use. "burgundsk" scoped is 1 result, because the
 *     titles say Bourgogne. Shopify's own synonyms cannot fix that here:
 *     "Synonyms aren't used when a search query contains search syntax", and so
 *     are product boosts — every query this file builds is search syntax, so
 *     the Search & Discovery synonym list is inert on this storefront no matter
 *     what is typed into it.
 *
 *     So synonyms are applied here, from a list the merchant edits in Theme
 *     settings -> Vyhľadávanie (window.VinoSearchSynonyms). Measured live:
 *     "burgundsk" 1 -> 6 results, "šedé" 9 -> 21, and every one of them a wine
 *     that really is what was asked for.
 *
 * Fallback ladder, once a search is submitted (sections/main-search.liquid
 * drives it, escalate() below builds each rung):
 *
 *   precise (scoped + synonyms) -> full-text strict -> OR of the same terms
 *   -> empty page, and predictive-fuzzy.js offers "Mali ste na mysli…".
 *
 * The raw query is never a rung: handing it back to Shopify is what produced
 * the junk in the first place.
 *
 * Three routes reach the results page and all three are rewritten here: the
 * search form, the predictive dropdown's "search for X" link, and a raw URL
 * landed on directly, which is re-requested strictly on load.
 */
(function () {
  var OPERATOR = /(?:^|\s)(?:AND|OR|NOT)(?:\s|$)/;

  /* The structured fields. Deliberately NOT the description: prose is what put
     Bordeaux wines under "burgundsk". Description matches are still shown, in
     their own block, built from strictQuery() instead. */
  var FIELDS = ['title', 'vendor', 'product_type', 'tag'];

  /* A scoped query is longer than a bare one by roughly (fields x variants), and
     Shopify rejects a query that grows without bound. Both ceilings are well
     above anything the synonym list produces in practice — "rulandské šedé"
     with three variants a side is ~640 characters — and when either is hit we
     fall back to the full-text form rather than send something unparseable. */
  var MAX_VARIANTS = 6;
  var MAX_QUERY_LENGTH = 1500;

  /* Two different tolerances, and the difference matters.

     SYNONYM_PREFIX is how much of a word has to be typed before it claims a
     synonym group: "burgundsk" is a real prefix of "burgundsko", so four
     letters is safe here — a prefix cannot drift.

     SHARED_STEM is the dropdown filter's inflection allowance, where the two
     words only have to START the same, and four letters turned out to be far
     too generous: "bourgogne" and "bourgneuf" share exactly four, so the very
     suggestion this fix exists to remove came back through the synonym. Six
     separates them and still keeps Slovak inflection, which changes the ending
     ("darčekové" / "Darčeková" share eight). */
  var SYNONYM_PREFIX = 4;

  /* Completion of a partial word against assets/search-vocabulary.js. */
  /* Four, matching SYNONYM_PREFIX and for the same reason: a short stub must
     not claim words it merely begins. At three, "pro" reached prosecco and
     provence and answered a Port search with sparkling wine. */
  var MIN_COMPLETION_PREFIX = 4;
  /* Two, not three. The vocabulary is frequency-ordered, so two completions is
     already the long tail: "bord" reaches bordeaux (883 products) and then
     bordova (4). A third only ever adds noise, and completions share the
     MAX_VARIANTS budget with the synonym group — "bord" is also a synonym for
     bordó, and at three completions that query carried five variants and
     twenty clauses. */
  var MAX_COMPLETIONS = 2;
  var SHARED_STEM = 6;

  // Leave anything the customer deliberately shaped alone: quoted phrases,
  // their own boolean operators, wildcards, field scopes like vendor:Gazin.
  function alreadyExplicit(query) {
    return (
      query.indexOf('"') !== -1 ||
      query.indexOf(':') !== -1 ||
      query.indexOf('*') !== -1 ||
      OPERATOR.test(query)
    );
  }

  /* Something Shopify can match on: a letter in any alphabet, or a digit.
     A token that is pure punctuation must be dropped rather than carried —
     the producer "Nichta - Víno Nichta" sends a bare "-", and "-*" reads as
     NOT-everything, which returned zero results for a producer with 43. */
  function meaningful(token) {
    for (var i = 0; i < token.length; i += 1) {
      var c = token.charAt(i);
      if (c >= '0' && c <= '9') return true;
      if (c.toLowerCase() !== c.toUpperCase()) return true;
    }
    return false;
  }

  /* Shopify splits a hyphenated name into two tokens, so "Saint-Pierre*"
     matches "Saintes Pierres de Nalys". Quoting keeps it whole and exact;
     proper names are not inflected, so nothing is lost by not stemming. */
  function term(token) {
    if (token.indexOf('-') !== -1) return '"' + token.replace(/"/g, '') + '"';
    return token + '*';
  }

  function tokens(raw) {
    var query = String(raw == null ? '' : raw).trim().replace(/\s+/g, ' ');
    if (!query) return [];
    return query.split(' ').filter(meaningful);
  }

  /* Accent- and case-insensitive, so a synonym written "Rulandské šedé" is
     found in a query typed "rulandske sede". */
  function fold(text) {
    return String(text == null ? '' : text)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  }

  /* ------------------------------------------------------------- synonyms */

  /* The merchant's list, one group per line, terms separated by commas:
   *
   *   rulandské šedé, pinot gris, pinot grigio
   *   burgundsko, burgundské, bourgogne
   *
   * A group is symmetric — typing any member searches for all of them. A term
   * may be several words ("pinot gris"), which is matched as a phrase.
   * Anything unparseable in the setting is skipped rather than thrown, so a
   * stray line in the theme editor can never take search down. */
  function groups() {
    /* Two sources, both optional, concatenated.
     *
     *   assets/synonyms.js   the bulk list, thousands of groups, versioned in
     *                        the repo and reviewed like code
     *   theme settings       the merchant's own quick edits from the admin
     *
     * The file exists because Shopify's Search & Discovery synonyms tab
     * disappeared from the admin in September 2026 with no changelog entry and
     * no way to export what was in it — not through the Admin API, not as a
     * metafield, not via Matrixify. The native ceiling was 1000 synonyms per
     * shop anyway. A file in the theme has no ceiling and nobody can withdraw
     * it. The setting stays so small corrections do not need a deploy. */
    var raw = [window.VinoSynonymsFile, window.VinoSearchSynonyms]
      .filter(function (part) { return typeof part === 'string' && part.trim(); })
      .join('\n');
    if (!raw.trim()) return [];

    var out = [];
    raw.split(/[\r\n]+/).forEach(function (line) {
      var members = line
        .split(',')
        .map(function (m) { return m.trim().replace(/\s+/g, ' '); })
        .filter(function (m) { return m && meaningful(m); });
      if (members.length > 1) out.push(members);
    });
    return out;
  }

  /* Split the query into units, where a unit is one word or a run of words that
     matched a synonym phrase, together with the variants it should also search
     for. Longest phrase wins, so "rulandské šedé" is one unit rather than two
     words that each drag in their own group. */
  function units(rawTokens) {
    var all = groups();
    var folded = rawTokens.map(fold);
    var out = [];
    var i = 0;

    /* Phrases first, longest to shortest, so the two-word group is preferred
       over a one-word member of some other group. */
    var phrases = [];
    all.forEach(function (members) {
      members.forEach(function (member) {
        var words = fold(member).split(' ').filter(Boolean);
        if (words.length > 1) phrases.push({ words: words, members: members });
      });
    });
    phrases.sort(function (a, b) { return b.words.length - a.words.length; });

    while (i < rawTokens.length) {
      var matched = null;

      for (var p = 0; p < phrases.length && !matched; p += 1) {
        var words = phrases[p].words;
        if (i + words.length > rawTokens.length) continue;
        var same = true;
        for (var w = 0; w < words.length; w += 1) {
          if (folded[i + w] !== words[w]) { same = false; break; }
        }
        if (same) matched = phrases[p];
      }

      if (matched) {
        out.push({
          text: rawTokens.slice(i, i + matched.words.length).join(' '),
          variants: matched.members,
        });
        i += matched.words.length;
        continue;
      }

      /* A single word: pull in its group's other members, but only the
         single-word ones — a phrase variant of a one-word query would search
         for words the customer never typed.

         A member counts as typed when the word matches it OR starts it, from
         four letters up. Everything else here matches on a prefix (Shopify
         matches "burgundsk*" as one), and the customer who reported this
         stopped at "burgundsk" — without the prefix rule the group holding
         "burgundsko" and "bourgogne" would never fire for exactly the query
         that needs it. Four letters keeps a short word from claiming a group
         it merely begins with. */
      var variants = [rawTokens[i]];
      var typedWord = folded[i];
      all.forEach(function (members) {
        var singles = members.filter(function (m) { return m.indexOf(' ') === -1; });
        var exact = singles.some(function (m) { return fold(m) === typedWord; });
        var hit = singles.some(function (m) {
          var member = fold(m);
          return (
            member === typedWord ||
            (typedWord.length >= SYNONYM_PREFIX && member.indexOf(typedWord) === 0)
          );
        });
        if (!hit) return;
        singles.forEach(function (m) {
          if (fold(m) === typedWord) return;
          if (variants.length >= MAX_VARIANTS) return;
          variants.push(m);
        });

        /* An EXACT one-word hit also earns the group's phrase members, quoted.
           "Welschriesling" is the only one-word member of its group, so under
           the rule above it pulled in nothing and the page came back empty —
           the wines are called "Rizling vlassky" and carry no other name.
           Exact only: a prefix like "pinot" must not drag in every Pinot
           phrase there is. scopedGroup() quotes them, so they cannot be split
           and matched apart. */
        if (!exact) return;
        members.forEach(function (m) {
          if (m.indexOf(' ') === -1) return;
          if (variants.length >= MAX_VARIANTS) return;
          variants.push(m);
        });
      });

      /* Half a word becomes a whole one.
       *
       * Shopify cannot match part of a word — its own manual says so, and the
       * `*` this file appends is inert on /search (measured 2026-09-21:
       * q=chardon and q=chardon* both return 90 of 779 Chardonnays). So the
       * fix cannot live in the query syntax; it has to happen before Shopify
       * sees the query. assets/search-vocabulary.js holds every word the
       * catalogue actually contains, frequency-first, so "doud" resolves to
       * "doudet" and "naud" to "naudin" and Shopify is then asked something it
       * can genuinely answer — with its own ranking, filters and stock rules
       * left completely alone.
       *
       * These are added as variants, never as replacements: the word the
       * customer typed stays in the query and keeps its own results. */
      completionsOf(typedWord).forEach(function (word) {
        if (variants.length >= MAX_VARIANTS) return;
        if (variants.some(function (v) { return fold(v) === word; })) return;
        variants.push(word);
      });

      out.push({ text: rawTokens[i], variants: variants });
      i += 1;
    }

    return out;
  }

  /* Catalogue words starting with what was typed, best first.
   *
   * MIN_COMPLETION_PREFIX stops a two-letter stub claiming the catalogue:
   * "ri" would otherwise pull in rizling, rivesaltes, ribera and riesling at
   * once. MAX_COMPLETIONS keeps the generated query from ballooning — every
   * variant becomes four more clauses in scopedGroup().
   *
   * An exact hit returns nothing: the word already exists, Shopify will find
   * it, and completing it further would only widen the search. */
  function completionsOf(typedWord) {
    var vocabulary = window.VinoVocabulary;
    if (!Array.isArray(vocabulary)) return [];
    if (!typedWord || typedWord.length < MIN_COMPLETION_PREFIX) return [];

    /* A word the catalogue already contains is never completed — the customer
       typed something real and Shopify will find it. This has to be checked
       across the WHOLE list before collecting anything: the list is ordered by
       frequency, not alphabetically, so "pro" is preceded by prosecco and
       provence and a scan that stops at MAX_COMPLETIONS never reaches it. */
    if (vocabulary.indexOf(typedWord) !== -1) return [];

    var out = [];
    for (var i = 0; i < vocabulary.length && out.length < MAX_COMPLETIONS; i += 1) {
      var word = vocabulary[i];
      if (word.length > typedWord.length && word.indexOf(typedWord) === 0) out.push(word);
    }
    return out;
  }

  /* --------------------------------------------------------------- queries */

  /* One unit becomes "(title:a* OR vendor:a* OR ... OR title:b* OR ...)" —
     every field, every variant. A multi-word variant travels quoted, because
     Shopify would otherwise split it and match the words apart. */
  function scopedGroup(unit) {
    var clauses = [];
    unit.variants.slice(0, MAX_VARIANTS).forEach(function (variant) {
      var phrase = variant.indexOf(' ') !== -1;
      var value = phrase
        ? '"' + variant.replace(/"/g, '') + '"'
        : term(variant);
      fieldsFor(phrase ? variant : null).forEach(function (field) {
        clauses.push(field + ':' + value);
      });
    });
    if (!clauses.length) return null;
    return '(' + clauses.join(' OR ') + ')';
  }

  /* Shopify has no phrase search. field:"a b" is answered whenever both words
     sit in that field, in any order and any distance apart, so a phrase can be
     asked safely of one field and be badly misread in the next. Which is which
     is a property of the catalogue, not of this file: it is measured over the
     whole of it by scripts/build-search-phrase-rules.mjs and shipped as
     assets/search-phrase-rules.js.

     Measured on the live shop 2026-09-22, title:"pinot noir" was exact across
     24 wines while product_type:"rulandsk\u00e9 biele" answered with Pinot Grigio,
     whose type path holds "biele" and "rulandsk\u00e9" far apart. That is why
     Weissburgunder \u2014 which is Pinot Blanc \u2014 offered five Pinot Gris wines.

     With no rules file shipped, every phrase keeps all four fields, which is
     exactly the behaviour before this existed. */
  function ruleFor(phrase) {
    var rules = window.VinoPhraseRules;
    if (!phrase || !rules) return null;
    return rules[phrase.toLowerCase()] || null;
  }

  function fieldsFor(phrase) {
    var rule = ruleFor(phrase);
    return rule && rule.fields && rule.fields.length ? rule.fields : FIELDS;
  }

  /* What a phrase drags in that it does not describe, named by another group's
     phrase rather than by a word picked to fit: the champagne "Grand Cru Blanc
     de Pinot Noir" leaves a Pinot Blanc search because it genuinely IS a Pinot
     Noir. Subtracting a variety keeps meaning something after the catalogue
     changes; subtracting "champagne" would not. */
  function exclusionsFor(unitList) {
    var seen = [];
    unitList.forEach(function (unit) {
      unit.variants.slice(0, MAX_VARIANTS).forEach(function (variant) {
        var rule = ruleFor(variant.indexOf(' ') !== -1 ? variant : null);
        if (!rule || !rule.exclude) return;
        rule.exclude.forEach(function (phrase) {
          if (seen.indexOf(phrase) === -1) seen.push(phrase);
        });
      });
    });
    if (!seen.length) return null;

    var clauses = [];
    seen.forEach(function (phrase) {
      var value = '"' + phrase.replace(/"/g, '') + '"';
      FIELDS.forEach(function (field) { clauses.push(field + ':' + value); });
    });
    return '(' + clauses.join(' OR ') + ')';
  }

  /* The precise query: scoped to the structured fields, synonyms expanded.
     No doubling of a single term is needed here — a scoped group is already
     several terms as far as Shopify is concerned, so relaxation does not fire
     on it (verified live: "(title:Bourgogne* OR ...)" returns 5, not 400). */
  function preciseQuery(raw) {
    var query = String(raw == null ? '' : raw).trim().replace(/\s+/g, ' ');
    if (!query || alreadyExplicit(query)) return null;

    var list = tokens(query);
    if (!list.length) return null;

    var parts = [];
    var unitList = units(list);
    unitList.forEach(function (unit) {
      var group = scopedGroup(unit);
      if (group) parts.push(group);
    });
    if (!parts.length) return null;

    var built = parts.join(' AND ');

    /* Left unbracketed on purpose. search-extra.js builds the "found in the
       description" block as "<full-text> AND NOT <precise>", so this trails as
       another AND NOT there and the excluded wine stays out of that block too. */
    var without = exclusionsFor(unitList);
    if (without) built += ' AND NOT ' + without;

    if (built.length <= MAX_QUERY_LENGTH) return built;

    /* Too long. Returning null here would send the customer back to Shopify's
       own fuzzy search — the very bug this file exists to remove — and it
       would do it silently. So the query is narrowed instead, synonyms first,
       because a scoped query without synonyms still beats an unscoped one. */
    var narrowed = [];
    units(list).forEach(function (unit) {
      var group = scopedGroup({ text: unit.text, variants: [unit.text] });
      if (group) narrowed.push(group);
    });
    built = narrowed.join(' AND ');
    return built && built.length <= MAX_QUERY_LENGTH ? built : null;
  }

  /* The full-text query — every field including the description. Still strict:
     this is the 2026-08 fix, unchanged, and it is what the "found in the
     description" block and the first fallback rung are built from. */
  function strictQuery(raw) {
    var query = String(raw == null ? '' : raw).trim().replace(/\s+/g, ' ');
    if (!query || alreadyExplicit(query)) return null;

    var terms = [];
    tokens(query).forEach(function (token) { terms.push(term(token)); });
    if (!terms.length) return null;

    /* Relaxation fires on a query Shopify reads as a single term — "Laroche"
       alone returned 17 products, 16 of them other Bordeaux châteaux. Sending
       the one term twice gives it the AND it needs and matches the same
       products either way. */
    if (terms.length === 1) terms.push(terms[0]);

    return terms.join(' AND ');
  }

  /* The OR rung: the same terms, any one of them. "gin Bombay" finds the five
     real gins this way instead of Shopify's fifteen unrelated products. */
  function broadQuery(raw) {
    var strict = strictQuery(raw);
    if (!strict) return null;
    return strict.split(' AND ').join(' OR ');
  }

  /* Which rung a rendered page is on, read from its own query string. A quoted
     phrase is a rung ABOVE precise: Shopify's phrase match is brittle — it
     returned nothing for "Rariga - Vinárstvo Rariga" (repeated word) and for
     "Pavelka a syn" (the stopword "a") even though both are real vendors — so
     an empty phrase page must fall through to the scoped query, not die into
     the fuzzy fallback. */
  function stageOf(query) {
    if (!query) return null;
    if (query.indexOf('title:') !== -1) return 'precise';
    if (query.indexOf(' OR ') !== -1) return 'broad';
    if (query.indexOf(' AND ') !== -1) return 'full';
    if (query.indexOf('"') !== -1) return 'phrase';
    return null;
  }

  /* The next rung down, given the terms as typed and the rung that just came
     back empty. Returns null at the bottom, where the page stays empty and
     predictive-fuzzy.js takes over with "Mali ste na mysli…". */
  function escalate(rawTerms, stage) {
    if (stage === 'phrase') return { q: preciseQuery(rawTerms), stage: 'precise' };
    if (stage === 'precise') return { q: strictQuery(rawTerms), stage: 'full' };
    if (stage === 'full') return { q: broadQuery(rawTerms), stage: 'broad' };
    return null;
  }

  /* ------------------------------------------------- the predictive dropdown
   *
   * Everything above is search syntax, and Shopify's predictive endpoint does
   * not accept any of it — "burgundsk* AND burgundsk*" returns nothing there,
   * so the dropdown cannot be made strict the way the results page was. Its
   * typo tolerance is therefore still on, and it is what the customer saw on
   * 2026-09-01: typing "burgundsk" suggested Bourgneuf 2020, two Bordeaux sets
   * and Certan De May — not one of them carries the word.
   *
   * So the answer is filtered instead of the question: a suggestion survives
   * only if the product really carries every word typed (or a synonym of it) in
   * its title, vendor, type or tags. The fields come from predictive-fuzzy.js's
   * index, which is built from /products.json and cached for a day.
   *
   * Two deliberate soft edges. A handle the index has never seen is KEPT — a
   * stale index must not hide a new wine. And matching allows a shared stem
   * (SHARED_STEM), not just a prefix, because Shopify stems both sides and a
   * stricter filter than the engine would drop "Darčeková taška" from
   * "darčekové".
   */
  function tokenMatches(entryTokens, part) {
    for (var i = 0; i < entryTokens.length; i += 1) {
      var token = entryTokens[i];
      if (token.indexOf(part) === 0) return true;
      if (
        part.length >= SHARED_STEM &&
        token.length >= SHARED_STEM &&
        part.slice(0, SHARED_STEM) === token.slice(0, SHARED_STEM)
      ) {
        return true;
      }
    }
    return false;
  }

  /* The words of a phrase inside one field value, in order, at most PHRASE_GAP
     words apart — the "genuine" test of scripts/build-search-phrase-rules.mjs,
     so the dropdown and the results page hold a phrase to the same standard. */
  var PHRASE_GAP = 2;

  function inOrder(words, parts) {
    var at = -1;
    for (var p = 0; p < parts.length; p += 1) {
      var next = -1;
      for (var i = at + 1; i < words.length; i += 1) {
        if (tokenMatches([words[i]], parts[p])) { next = i; break; }
      }
      if (next === -1) return false;
      if (at !== -1 && next - at - 1 > PHRASE_GAP) return false;
      at = next;
    }
    return true;
  }

  /* One unit is satisfied by any one of its variants, and a multi-word variant
     only when every word of it lands — inside ONE field value when the index
     keeps them apart (`svals`). Pooled into one bag, the tags "Rulandské šedé"
     and "Biele víno" spelled "rulandské biele", and typing Weissburgunder
     suggested two Pinot Gris out of three (measured 2026-09-23: 26 of the
     group's 55 suggestable products were Pinot Gris or a Blanc de Pinot Noir).
     An entry without `svals` keeps the bag, which is how this behaved before. */
  function unitMatches(entry, unit) {
    var entryTokens = entry.stoks || [];
    return unit.variants.some(function (variant) {
      var parts = fold(variant).split(' ').filter(Boolean);
      if (!parts.length) return false;
      if (parts.length > 1 && entry.svals) {
        return entry.svals.some(function (words) { return inOrder(words, parts); });
      }
      return parts.every(function (part) { return tokenMatches(entryTokens, part); });
    });
  }

  function handleOf(href) {
    var match = /\/products\/([^/?#]+)/.exec(href || '');
    return match ? match[1] : null;
  }

  /* Returns true when something was removed, so the caller can decide to offer
     the fuzzy suggestions instead of an emptied dropdown.
   *
   * `options.require` is 'all' by default — every word has to land, the same
   * standard the results page holds a product to. 'any' is the dropdown's last
   * rung before giving up, and it exists because "darčekový kôš" has no product
   * carrying BOTH words in a structured field: the results page answers it with
   * twelve products by falling back, and an empty dropdown over that is a
   * regression, not precision. It mirrors the page's OR rung. */
  /* ------------------------------------------ collection suggestions
   *
   * The dropdown also lists collections, and Shopify picks those with the same
   * typo tolerance: typing "burgun" offered Burgenland next to Burgundy
   * (2026-09-24, reported by the store owner). A collection stays only if its
   * title carries what was typed.
   *
   * Looser than the product rule on purpose. Collection titles mix Slovak with
   * the region's own name, and Slovak changes the end of a word: "Veltlín
   * zelený" must stay for "veltlínske zelené", "Hotové darčekové balíčky" for
   * "balíček", "Cockburn's Port" for "porto". So a word matches when either one
   * starts with the other, or when they share at least five letters and differ
   * in no more than the last two typed. Measured on 182 queries against the
   * live shop (2026-09-25): of 231 collection suggestions this removes
   * Burgenland for "burgun" and Slovensko / Slovinsko for each other, and
   * nothing else except Alsace for the half-typed "alsas". */
  function sharedPrefix(a, b) {
    var i = 0;
    while (i < a.length && i < b.length && a.charAt(i) === b.charAt(i)) i += 1;
    return i;
  }

  function titleWords(text) {
    return fold(text).replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
  }

  function titleWordMatches(words, part) {
    if (tokenMatches(words, part)) return true;
    for (var i = 0; i < words.length; i += 1) {
      var word = words[i];
      if (word.length >= 4 && part.indexOf(word) === 0) return true;
      if (sharedPrefix(word, part) >= Math.max(5, part.length - 2)) return true;
    }
    return false;
  }

  function collectionMatches(words, unit) {
    return unit.variants.some(function (variant) {
      var parts = titleWords(variant);
      return parts.length > 0 && parts.every(function (part) { return titleWordMatches(words, part); });
    });
  }

  /* Needs no index — the title is right there in the link. */
  function filterCollections(section, list, wanted) {
    var links = section.querySelectorAll('a.search-item[href*="/collections/"]');
    Array.prototype.forEach.call(links, function (link) {
      var href = link.getAttribute ? link.getAttribute('href') || '' : '';
      if (href.indexOf('/collections/') === -1) return;
      var words = titleWords(link.textContent || '');
      if (!words.length) return;
      if (list[wanted](function (unit) { return collectionMatches(words, unit); })) return;

      var block = link.parentNode;
      if (!block) return;
      block.removeChild(link);
      /* A collections block left empty goes too, or the dropdown opens on a
         blank band above "Produkty". */
      if (!block.querySelector('[data-js-search-item]') && block.parentNode) {
        block.parentNode.removeChild(block);
      }
    });
  }

  function filterDropdown(section, rawQuery, options) {
    if (!section) return false;

    var list = units(tokens(rawQuery));
    if (!list.length) return false;

    var wanted = options && options.require === 'any' ? 'some' : 'every';
    filterCollections(section, list, wanted);

    var map = window.VinoFuzzy && window.VinoFuzzy.handles();
    if (!map) return false; /* index not built yet — Shopify's answer stands */

    var items = section.querySelectorAll('.cart-item[data-js-search-item]');
    var removed = 0;
    var kept = 0;

    Array.prototype.forEach.call(items, function (item) {
      var link = item.querySelector('a[href*="/products/"]');
      var entry = link && map[handleOf(link.getAttribute('href'))];
      if (!entry) { kept += 1; return; }

      var matched = list[wanted](function (unit) { return unitMatches(entry, unit); });
      if (matched) { kept += 1; return; }

      if (item.parentNode) item.parentNode.removeChild(item);
      removed += 1;
    });

    /* A block left holding nothing but its own "Produkty" heading reads as a
       bug, so it goes with the last item. */
    if (removed && !kept) {
      Array.prototype.forEach.call(section.querySelectorAll('.search-block'), function (blockEl) {
        if (!blockEl.querySelector('[data-js-search-item]') && blockEl.parentNode) {
          blockEl.parentNode.removeChild(blockEl);
        }
      });
    }

    return removed > 0;
  }

  /* The same query with one word swapped for a synonym of it, as plain text —
     for the predictive dropdown, which takes no syntax and so cannot be asked
     for a word and its synonyms at once.
   *
   * It is needed because Shopify's own suggestions for "burgundsk" were nine
   * products and not one of them a Burgundy: the filter above correctly removed
   * all nine, and an empty dropdown over a results page holding five right
   * answers is its own kind of wrong. Asking again for "bourgogne" fills it.
   *
   * Ordered as the merchant wrote the group, and capped — this runs while
   * someone is typing. */
  function alternates(raw, limit) {
    var list = tokens(raw);
    if (!list.length) return [];

    var parts = units(list);
    var out = [];
    var max = limit || 2;

    parts.forEach(function (unit, index) {
      unit.variants.forEach(function (variant) {
        if (out.length >= max) return;
        if (fold(variant) === fold(unit.text)) return;

        /* A variant that starts like the typed word adds nothing: Shopify
           matches on a prefix, so it already offered whatever "burgundské"
           would offer for "burgundsk". Only a genuinely different word is
           worth a second request — "bourgogne" is the one that fills that
           dropdown, and it sits behind three inflections in the group. */
        var typedStem = fold(unit.text).slice(0, SHARED_STEM);
        var variantStem = fold(variant).slice(0, SHARED_STEM);
        if (typedStem.length === SHARED_STEM && typedStem === variantStem) return;

        var swapped = parts.map(function (other, i) {
          return i === index ? variant : other.text;
        });
        out.push(swapped.join(' '));
      });
    });

    return out;
  }

  window.VinoStrictSearch = {
    filterDropdown: filterDropdown,
    alternates: alternates,
    strictQuery: strictQuery,
    preciseQuery: preciseQuery,
    broadQuery: broadQuery,
    stageOf: stageOf,
    escalate: escalate,
    tokens: tokens,
    fold: fold,
    units: units,
    fields: FIELDS,
  };

  function rewrite(value) {
    try {
      return preciseQuery(value);
    } catch (e) {
      return null; /* leave the raw query in place — search still works */
    }
  }

  /* The results page shows products, and only products: articles used to be
     interleaved with them by relevance, so a search for "šedé" listed 13 wines,
     then 11 blog posts, then two more wines on page two. assets/search-extra.js
     re-adds the articles under the products, where they were asked for. */
  function productsOnly(url) {
    url.searchParams.set('type', 'product');
  }

  /* The form is not the only way into the results page. A link from Google, a
     shared or bookmarked URL and the back button all arrive with the query
     exactly as it was typed, and those bypassed the rewrite entirely: landing
     straight on "?q=Domaine Laroche" returned 47 products where the search box
     returns 1. Shopify has already rendered that loose page by the time this
     runs, so re-request it strictly — every route in now gets one answer.

     No loop is possible: the rewritten query carries wildcards, field scopes and
     AND, which preciseQuery() refuses as alreadyExplicit and returns null for.
     The same null covers an empty search and the lower rungs of the ladder. */
  (function redirectRawQuery() {
    try {
      if (window.location.pathname.indexOf('/search') === -1) return;

      var url = new URL(window.location.href);
      var strict = rewrite(url.searchParams.get('q'));
      if (!strict) return;

      url.searchParams.set('q', strict);
      productsOnly(url);
      /* replace, not assign — the raw URL must not become a back-button stop
         that bounces the customer straight forward again. */
      window.location.replace(url.toString());
    } catch (e) {
      /* leave the page as Shopify rendered it */
    }
  })();

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      if ((form.getAttribute('action') || '').indexOf('/search') === -1) return;

      var input = form.querySelector('input[name="q"], input[data-js-typed-q]');
      if (!input) return;

      var hidden = form.querySelector('input[data-js-strict-q]');
      var strict = rewrite(input.value);

      if (!strict) {
        /* Nothing to rewrite — an empty box, or a query the customer shaped
           themselves. A hidden field left over from an earlier submit would
           send a second, stale `q`. */
        if (hidden && hidden.parentNode) hidden.parentNode.removeChild(hidden);
        input.setAttribute('name', 'q');
        return;
      }

      /* The query Shopify needs is long and full of operators, and it used to
         be written straight into the box the customer types in — where the
         browser then restored it after the page loaded, so they were left
         looking at "(title:burgundsk* OR vendor:burgundsk* OR ...". Reported
         2026-09-04.

         So the box is never touched. The strict query travels in a hidden
         field, and the visible input loses its name for the duration of the
         submit so its raw value is not sent as a second `q`. The name is put
         back on the next tick — the form data is serialised before any timeout
         runs, and the field stays usable if the navigation never happens. */
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'q';
        hidden.setAttribute('data-js-strict-q', '');
        form.appendChild(hidden);
      }
      hidden.value = strict;

      input.setAttribute('data-js-typed-q', '');
      input.removeAttribute('name');
      setTimeout(function () { input.setAttribute('name', 'q'); }, 0);

      if (!form.querySelector('input[name="type"]')) {
        var type = document.createElement('input');
        type.type = 'hidden';
        type.name = 'type';
        type.value = 'product';
        form.appendChild(type);
      }
    },
    true
  );

  /* The predictive dropdown's "search for X" link bypasses the form, so it
     carries the query as typed and is rewritten here on the way out. Doing it
     here rather than in Liquid keeps one implementation of the rules — and
     avoids `predictive_search.terms` arriving HTML-escaped, which quietly
     broke the Liquid version's quote handling once already. */
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target && event.target.closest && event.target.closest('[data-js-strict-search]');
      if (!link) return;

      try {
        var url = new URL(link.href, window.location.origin);
        var strict = rewrite(url.searchParams.get('q'));
        if (!strict) return;
        url.searchParams.set('q', strict);
        productsOnly(url);
        link.href = url.toString();
      } catch (e) {
        /* the link still points at the raw query */
      }
    },
    true
  );
})();
